import React, { useState, useEffect } from 'react';
import { Award, Percent, Calendar, CreditCard, Truck, Star, Edit2, Save, X } from 'lucide-react';
import { getPriceTiers, updatePriceTier } from '../../api/wholesale/pricing';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const PricingTiersPage = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTier, setEditingTier] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchTiers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPriceTiers();
      const data = response?.data || response || [];
      setTiers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching pricing tiers:', err);
      setError('Failed to load pricing tiers');
      setTiers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiers();
  }, []);

  const handleEdit = (tier) => {
    setEditingTier(tier.id);
    setEditForm({
      tier_name: tier.tier_name,
      tier_level: tier.tier_level,
      discount_percentage_min: tier.discount_percentage_min,
      discount_percentage_max: tier.discount_percentage_max,
      default_discount_percentage: tier.default_discount_percentage,
      default_payment_terms: tier.default_payment_terms,
      max_payment_terms: tier.max_payment_terms,
      default_credit_limit: tier.default_credit_limit,
      max_credit_limit: tier.max_credit_limit,
      minimum_order_value: tier.minimum_order_value,
      free_delivery_threshold: tier.free_delivery_threshold,
      benefits_description: tier.benefits_description
    });
  };

  const handleCancelEdit = () => {
    setEditingTier(null);
    setEditForm({});
  };

  const handleSave = async () => {
    try {
      await updatePriceTier(editingTier, editForm);
      setEditingTier(null);
      setEditForm({});
      fetchTiers();
    } catch (err) {
      console.error('Error updating tier:', err);
      setError(err.message || 'Failed to update tier');
    }
  };

  const tierColorClasses = {
    1: 'bg-amber-600',
    2: 'bg-gray-400',
    3: 'bg-yellow-500',
    4: 'bg-purple-600'
  };

  const tierNames = {
    1: 'Bronze',
    2: 'Silver',
    3: 'Gold',
    4: 'Platinum'
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Pricing Tiers Configuration</h1>
        <p className="text-gray-500 mt-1">
          Manage Bronze, Silver, Gold, and Platinum tier settings
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Tiers Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {tiers.map((tier) => {
          const isEditing = editingTier === tier.id;
          const colorClass = tierColorClasses[tier.tier_level] || 'bg-gray-500';

          return (
            <div
              key={tier.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Tier Header */}
              <div className={`${colorClass} text-white p-6`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Award className="h-8 w-8" />
                    <div>
                      <h2 className="text-2xl font-bold">{tier.tier_name}</h2>
                      <p className="text-white/80 text-sm">Level {tier.tier_level}</p>
                    </div>
                  </div>
                  {!isEditing && (
                    <button
                      onClick={() => handleEdit(tier)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors text-sm font-medium"
                    >
                      <Edit2 className="h-4 w-4" />
                      Edit
                    </button>
                  )}
                </div>
              </div>

              {/* Tier Details */}
              <div className="p-6 space-y-4">
                {isEditing ? (
                  /* Edit Mode */
                  <div className="space-y-4">
                    {/* Discount Range */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Min Discount %
                        </label>
                        <input
                          type="number"
                          value={editForm.discount_percentage_min || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              discount_percentage_min: parseFloat(e.target.value)
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Default %
                        </label>
                        <input
                          type="number"
                          value={editForm.default_discount_percentage || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              default_discount_percentage: parseFloat(e.target.value)
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Discount %
                        </label>
                        <input
                          type="number"
                          value={editForm.discount_percentage_max || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              discount_percentage_max: parseFloat(e.target.value)
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>

                    {/* Payment Terms */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Default Payment Terms (days)
                        </label>
                        <input
                          type="number"
                          value={editForm.default_payment_terms || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              default_payment_terms: parseInt(e.target.value)
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Payment Terms (days)
                        </label>
                        <input
                          type="number"
                          value={editForm.max_payment_terms || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              max_payment_terms: parseInt(e.target.value)
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>

                    {/* Credit Limits */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Default Credit Limit (₦)
                        </label>
                        <input
                          type="number"
                          value={editForm.default_credit_limit || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              default_credit_limit: parseFloat(e.target.value)
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Credit Limit (₦)
                        </label>
                        <input
                          type="number"
                          value={editForm.max_credit_limit || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              max_credit_limit: parseFloat(e.target.value)
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>

                    {/* Other Settings */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Min Order Value (₦)
                        </label>
                        <input
                          type="number"
                          value={editForm.minimum_order_value || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              minimum_order_value: parseFloat(e.target.value)
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Free Delivery Threshold (₦)
                        </label>
                        <input
                          type="number"
                          value={editForm.free_delivery_threshold || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              free_delivery_threshold: parseFloat(e.target.value)
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>

                    {/* Benefits */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Benefits Description
                      </label>
                      <textarea
                        value={editForm.benefits_description || ''}
                        onChange={(e) =>
                          setEditForm({ ...editForm, benefits_description: e.target.value })
                        }
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                      >
                        <Save className="h-4 w-4" />
                        Save Changes
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="space-y-4">
                    {/* Discount */}
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Percent className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Discount Range</p>
                        <p className="font-semibold text-gray-900">
                          {tier.discount_percentage_min}% - {tier.discount_percentage_max}%
                          <span className="text-sm text-gray-500 font-normal ml-2">
                            (Default: {tier.default_discount_percentage}%)
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Payment Terms */}
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Payment Terms</p>
                        <p className="font-semibold text-gray-900">
                          Net {tier.default_payment_terms} days (Max: {tier.max_payment_terms} days)
                        </p>
                      </div>
                    </div>

                    {/* Credit Limit */}
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Credit Limit</p>
                        <p className="font-semibold text-gray-900">
                          ₦{(tier.default_credit_limit || 0).toLocaleString()} (Max: ₦
                          {(tier.max_credit_limit || 0).toLocaleString()})
                        </p>
                      </div>
                    </div>

                    {/* Free Delivery */}
                    {tier.free_delivery_threshold && (
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Truck className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-500">Free Delivery</p>
                          <p className="font-semibold text-gray-900">
                            Orders above ₦{tier.free_delivery_threshold.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Benefits */}
                    {tier.benefits_description && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <Star className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Benefits</p>
                            <p className="text-sm text-gray-600">{tier.benefits_description}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Stats */}
                    {tier.distributor_count !== undefined && (
                      <div className="border-t pt-4">
                        <p className="text-sm text-gray-500">
                          Active Distributors:{' '}
                          <span className="font-semibold text-gray-900">
                            {tier.distributor_count || 0}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {tiers.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No pricing tiers found</p>
        </div>
      )}
    </div>
  );
};

export default PricingTiersPage;
